import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Home, Building2, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const EPCExplained = () => {
  return (
    <>
      <Helmet>
        <title>What is an EPC? Complete Guide to Energy Performance Certificates | Energy Performance Direct</title>
        <meta name="description" content="Complete guide to Energy Performance Certificates (EPCs). What they are, when you need one, how ratings work A-G, costs, validity, and how to improve your EPC score." />
        <meta name="keywords" content="what is an EPC, energy performance certificate explained, EPC ratings, EPC guide, EPC requirements, improve EPC rating" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/resources/epc-explained" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <Link to="/resources" className="text-green-600 hover:underline text-sm">← Back to Resources</Link>
              </div>
              
              <header className="mb-12">
                <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">EPC Basics</span>
                <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-6">What is an EPC? Complete Guide to Energy Performance Certificates</h1>
                <p className="text-xl text-gray-600">Everything you need to know about EPCs - what they are, when you need one, and how to get the best rating for your property.</p>
              </header>

              <section className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold mt-8 mb-4">What is an Energy Performance Certificate?</h2>
                <p className="text-gray-700 mb-4">
                  An Energy Performance Certificate (EPC) is a document that shows how energy efficient a property is. It rates the property from A (most efficient) to G (least efficient) and provides an estimate of energy costs along with recommendations for improvements.
                </p>
                <p className="text-gray-700 mb-4">
                  EPCs were introduced in England and Wales in 2007 as part of Home Information Packs, and became a standalone legal requirement in 2008. They are produced by qualified Domestic Energy Assessors (DEAs) for homes or Non-Domestic Energy Assessors (NDEAs) for commercial properties.
                </p>

                <h2 className="text-2xl font-bold mt-8 mb-4">When Do You Need an EPC?</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Home className="h-5 w-5 text-green-600" /> Residential
                      </h3>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> Selling a property</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> Renting out a property</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> New build completion</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> After certain modifications</li>
                      </ul>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-green-600" /> Commercial
                      </h3>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> Selling commercial property</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> Letting commercial property</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> New commercial buildings</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> MEES compliance checks</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Understanding EPC Ratings</h2>
                <p className="text-gray-700 mb-4">
                  EPC ratings range from A (most efficient, 92-100 points) to G (least efficient, 1-20 points). The rating is based on the property's energy efficiency per square metre, calculated using standardised assumptions about usage.
                </p>
                <div className="my-6 overflow-hidden rounded-lg border">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Rating</th>
                        <th className="px-4 py-3 text-left font-semibold">Score</th>
                        <th className="px-4 py-3 text-left font-semibold">What it Means</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr className="bg-green-50"><td className="px-4 py-3 font-bold text-green-800">A</td><td className="px-4 py-3">92-100</td><td className="px-4 py-3">Excellent - very energy efficient</td></tr>
                      <tr className="bg-green-50/50"><td className="px-4 py-3 font-bold text-green-700">B</td><td className="px-4 py-3">81-91</td><td className="px-4 py-3">Very good efficiency</td></tr>
                      <tr className="bg-yellow-50"><td className="px-4 py-3 font-bold text-yellow-700">C</td><td className="px-4 py-3">69-80</td><td className="px-4 py-3">Good - above average</td></tr>
                      <tr className="bg-yellow-50/50"><td className="px-4 py-3 font-bold text-yellow-600">D</td><td className="px-4 py-3">55-68</td><td className="px-4 py-3">Average for UK homes</td></tr>
                      <tr className="bg-orange-50"><td className="px-4 py-3 font-bold text-orange-600">E</td><td className="px-4 py-3">39-54</td><td className="px-4 py-3">Below average - minimum for rentals</td></tr>
                      <tr className="bg-red-50"><td className="px-4 py-3 font-bold text-red-600">F</td><td className="px-4 py-3">21-38</td><td className="px-4 py-3">Poor - illegal to rent</td></tr>
                      <tr className="bg-red-100"><td className="px-4 py-3 font-bold text-red-700">G</td><td className="px-4 py-3">1-20</td><td className="px-4 py-3">Very poor - illegal to rent</td></tr>
                    </tbody>
                  </table>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 my-6">
                  <div className="flex gap-3">
                    <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-amber-800 mb-2">MEES Regulations for Landlords</h3>
                      <p className="text-amber-700">Since 1 April 2023, all privately rented properties (domestic and commercial) in England and Wales must have an EPC rating of E or above unless a valid exemption is registered on the PRS Exemptions Register. Financial penalties for non-compliance are set out in the Regulations and calculated on rateable value and breach duration for non-domestic property (see Regulation 39 of the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015). See our <Link to="/mees-commercial-buildings" className="text-green-700 underline">MEES for Commercial Buildings</Link> guide for the June 2026 DESNZ update on the future EPC B target.</p>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">How to Improve Your EPC Rating</h2>
                <p className="text-gray-700 mb-4">Common improvements that can boost your EPC rating include:</p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>Loft insulation</strong> - often the most cost-effective improvement</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>Cavity wall insulation</strong> - significant impact on heat loss</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>Boiler upgrade</strong> - modern condensing boilers are more efficient</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>Double or triple glazing</strong> - reduces heat loss through windows</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>LED lighting</strong> - low cost, easy improvement</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>Heating controls</strong> - thermostats, TRVs, programmers</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>Renewable energy</strong> - solar PV, heat pumps</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">EPC Costs and Validity</h2>
                <p className="text-gray-700 mb-4">
                  A domestic EPC typically costs between £60-£120 depending on property size and location. Commercial EPCs start from around £180 and can be significantly more for larger or complex buildings.
                </p>
                <p className="text-gray-700 mb-4">
                  EPCs are valid for 10 years from the date of issue. You can use an existing valid EPC when selling or letting, though getting a new one may be worthwhile if you've made improvements.
                </p>

                <h2 className="text-2xl font-bold mt-8 mb-4">Related Services</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Link to="/domestic-epc-exeter" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Domestic EPCs in Exeter</h3>
                        <p className="text-gray-600 text-sm">Local EPC assessments from £120</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/commercial-epc-bristol" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Commercial EPCs</h3>
                        <p className="text-gray-600 text-sm">Non-domestic assessments from £180</p>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </section>

              <div className="mt-12 p-6 bg-green-50 rounded-lg text-center">
                <h3 className="text-xl font-bold mb-3">Need an EPC?</h3>
                <p className="text-gray-600 mb-4">Get a quote for your domestic or commercial property today.</p>
                <Link to="/booking">
                  <Button className="bg-green-600 hover:bg-green-700">
                    Book EPC Assessment <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default EPCExplained;
